import { useCallback, useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase, fromDateKey } from '@/lib/supabase';
import {
  downloadAbsenceHistoryCsv,
  loadDashboardData,
  notCountedLabel,
  unpaidWarningFor,
  type AbsenceFactRow,
  type DashboardData,
  type EmployeeDashboardRow,
} from './data';
import { approveRequest, denyRequest, type ActionResult } from '@/lib/actions';
import { TechnicalDetails } from '@/lib/TechnicalDetails';
import { TableScroller } from '@/lib/TableScroller';
import { TeamCalendar } from './TeamCalendar';
import { LogAbsence } from '@/features/log/LogAbsence';
import { ManageRecords } from '@/features/records/ManageRecords';
import { AdminConsole, ManagerRoster } from '@/features/admin/AdminConsole';
import { ErrorBoundary } from '@/lib/ErrorBoundary';
// Direction D redesign (2026-09-04): the shared sidebar/tab-bar shell and its
// style tokens/icons — see BUILD_PLAN's redesign notes. AppShell is generic
// (no Supabase/View knowledge of its own), so this is the only view wired
// into it this pass; the rest keep their pre-redesign header/back-link chrome
// until a follow-up wraps them too (a deliberate, called-out visual seam).
import { AppShell, type AppShellNavItem } from '@/lib/AppShell';
import { color, font, radius, shadow } from '@/lib/theme';
import { ChevronDown, ClipboardPlus, Grid, ListIcon, Settings, Users } from '@/lib/icons';
import { useIsNarrow } from '@/lib/useMediaQuery';

/**
 * Views, navigated by URL hash so back/refresh behave without adding a router
 * dependency (and hash routing needs no SPA-fallback config on any host —
 * relevant to open item #3).
 *
 * 2026-08-11, Scott's feedback: the Dashboard/Calendar/Activity three-way
 * split was over-slicing — one read-only page scrolls comfortably at this
 * team size. The old '#/calendar' and '#/activity' hashes stay as aliases so
 * stale links land on the dashboard, not on a surprise.
 *
 * Redesign follow-up (2026-09-04): Manager Home — a separate card-grid
 * landing screen outside AppShell's sidebar/tab-bar shell — is gone. '#/'
 * (and any other unrecognized hash) now resolves straight to 'dashboard',
 * the same as every other AppShell destination; there is no longer a
 * screen the sidebar's own nav list doesn't already cover.
 */
type View = 'dashboard' | 'log' | 'records' | 'admin' | 'roster';

function viewFromHash(): View {
  switch (window.location.hash) {
    case '#/log':
      return 'log';
    case '#/records':
      return 'records';
    case '#/admin':
      return 'admin';
    // Migration 0020: a location-scoped manager's own Roster page. Its own
    // hash rather than a tab inside '#/admin', because the admin console is
    // admin-only as a whole and this is the one area that no longer is.
    case '#/roster':
      return 'roster';
    // '#/dashboard' and the two retired hashes ('#/calendar', '#/activity')
    // all fall through here alongside '#/' itself and any unrecognized
    // hash — dashboard is the landing view now, so every one of those
    // aliases the same, single default.
    default:
      return 'dashboard';
  }
}

/**
 * The manager portal's main surface — P2's read-only dashboard, which P3
 * taught to write. Reads all flow through data.ts; the ONLY writes are the
 * definer RPCs in lib/actions.ts (approve/deny here, log_absence in its own
 * view) — the browser still holds zero table-level write privileges
 * (tests/005 pins that), so a bug in this page cannot invent a new mutation.
 *
 * Two windows still computed per employee, one shown: Q1 was DECIDED
 * 2026-08-20 — the 10/15/20 warnings AND the chip's colouring both run on
 * CALENDAR-YTD. The rolling 12-month number (`activePoints`) is no longer
 * rendered here (pre-P8 punch list: it drove nothing on screen and read as
 * a second, confusing score) but `data.ts` still computes it via
 * `active_points()`, kept available since it's a real, different window
 * from calendar-YTD. They differ by design; do not unify them.
 */

export function Dashboard({ session }: { session: Session }) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [view, setView] = useState<View>(viewFromHash);
  const [openEmployee, setOpenEmployee] = useState<string | null>(null);
  // P6: the admin location filter. '' = all locations. Deliberately its own
  // page-local state, not lifted or persisted — Manage Records keeps its own
  // independent copy, same as its existing employee/date filters.
  const [locationFilter, setLocationFilter] = useState('');
  // Bumped on every quiet refresh so the Team Calendar refetches its month
  // too — it fetches on its own and would otherwise keep showing a request
  // as Pending after the decision made in the section right below it.
  const [reloadKey, setReloadKey] = useState(0);
  // Direction D redesign: only the dashboard branch's own page padding reads
  // this, but hooks must run unconditionally every render (there are early
  // returns below), so it is called here rather than after them.
  const narrow = useIsNarrow();

  useEffect(() => {
    const onHash = () => setView(viewFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const load = useCallback(() => {
    setError(null);
    setData(null);
    loadDashboardData().then(setData, (e: unknown) => {
      // CLAUDE.md trap #19: right after sign-in, PostgREST can momentarily
      // reject a freshly-minted JWT as "issued at future" — a clock-sync
      // race between GoTrue minting the token and PostgREST validating its
      // timestamp a beat later. Harmless and always clears on retry, so
      // retry once, silently, before ever showing the scary error panel.
      if (String(e).includes('JWT issued at future')) {
        setTimeout(() => {
          loadDashboardData().then(setData, (e2: unknown) => setError(String(e2)));
        }, 1000);
        return;
      }
      setError(String(e));
    });
  }, []);

  useEffect(load, [load]);

  // P7: stamps users.last_seen once per Dashboard mount — fire-and-forget,
  // deliberately its OWN effect rather than folded into load()'s
  // Promise.all. That chain bail()s loudly on any error by design (an RLS
  // denial is zero rows, never an error — CLAUDE.md trap #3), which is
  // exactly wrong for a health-check side-signal: a hiccup here must never
  // turn into a blank dashboard. Powers the "manager never signed in" check.
  //
  // supabase-js query builders are LAZY thenables — the request is only
  // actually dispatched once something calls .then()/await on them. `void
  // supabase.rpc(...)` alone builds the request and discards it WITHOUT
  // ever sending it. The explicit .then(noop, noop) below is what triggers
  // the real network call, while still swallowing any failure silently
  // (never an unhandled rejection, never surfaced to the user).
  useEffect(() => {
    supabase.rpc('touch_last_seen').then(
      () => {},
      () => {},
    );
  }, []);

  // After a write (approve/deny/log): re-fetch WITHOUT blanking the page, so
  // the decision's result message stays readable while the numbers update
  // underneath it. Only a real failure clears the screen.
  const refresh = useCallback(() => {
    setReloadKey((k) => k + 1);
    loadDashboardData().then(setData, (e: unknown) => setError(String(e)));
  }, []);

  async function signOut() {
    setSigningOut(true);
    await supabase.auth.signOut();
    setSigningOut(false);
  }

  async function exportCsv(today: string) {
    setExporting(true);
    setExportError(null);
    try {
      await downloadAbsenceHistoryCsv(today);
    } catch (e: unknown) {
      setExportError(String(e));
    } finally {
      setExporting(false);
    }
  }

  if (error) {
    return (
      <main style={S.center}>
        <div style={S.panel}>
          <h2 style={S.panelH}>Could not load the dashboard</h2>
          {/* A real failure — network, config, a dropped grant. RLS problems
              do NOT land here; they show up as empty lists or as the
              no-access panel below. */}
          <p style={S.panelP}>
            <code style={S.code}>{error}</code>
          </p>
          <button onClick={load} style={S.button}>
            Try again
          </button>
        </div>
      </main>
    );
  }

  if (!data) return <main style={S.center}>Loading…</main>;

  // Signed in, but no ACTIVE managers row for this email. RLS returns zero
  // rows everywhere in this state, so without this panel the symptom would be
  // a fully rendered, entirely empty dashboard — trap #3 wearing a suit.
  if (!data.manager) {
    return (
      <main style={S.center}>
        <div style={S.panel}>
          <h2 style={S.panelH}>No manager access</h2>
          <p style={S.panelP}>
            You are signed in as <strong>{session.user.email}</strong>, but that
            address has no active manager record. If your access was just
            changed, this is immediate and expected — otherwise ask an admin to
            add you.
          </p>
          <button onClick={signOut} disabled={signingOut} style={S.button}>
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      </main>
    );
  }

  const { manager, employees, summary, recentFacts, queueFacts } = data;

  // Migration 0020's role predicate: the same isAdmin/canManageRoster split
  // that decides which two nav items the sidebar shows for this manager. Now
  // computed just once, right here — the only consumer since Manager Home's
  // own card grid (which used to need the identical predicate) is gone.
  const isAdmin = manager.role === 'admin';
  const canManageRoster = !isAdmin && manager.location_id !== null;
  const navItems: AppShellNavItem[] = [
    { key: 'dashboard', label: 'Dashboard', icon: <Grid />, href: '#/dashboard' },
    { key: 'log', label: 'Log an Absence', icon: <ClipboardPlus />, href: '#/log' },
    { key: 'records', label: 'Manage Records', icon: <ListIcon />, href: '#/records' },
  ];
  if (canManageRoster) {
    navItems.push({ key: 'roster', label: 'Roster', icon: <Users />, href: '#/roster' });
  }
  if (isAdmin) {
    navItems.push({ key: 'admin', label: 'Admin Console', icon: <Settings />, href: '#/admin' });
  }

  const nameById = new Map(employees.map((r) => [r.employee.id, r.employee.full_name]));
  const multiLocation = data.locations.length > 1;

  // P6: the location filter. Options are derived from the locations actually
  // present among the employees THIS manager can already see — RLS scopes a
  // regular manager's `employees` to one location, so this collapses to a
  // single entry for them and the control hides itself with no separate
  // role check needed. An admin's unscoped `employees` naturally yields every
  // location that has staff.
  const employeeLocationIds = new Set(employees.map((r) => r.employee.location_id));
  const filterableLocations = data.locations.filter((l) => employeeLocationIds.has(l.id));
  const showLocationFilter = filterableLocations.length > 1;

  // Adversarial-review fix: a refresh (any write) can shrink filterableLocations
  // out from under a selected value — its last employee got reassigned or
  // deactivated. Derive a SANITIZED value every render rather than trusting the
  // raw state directly, so a now-invalid selection falls back to "All
  // locations" instead of silently filtering everything to zero rows with the
  // control itself gone (showLocationFilter would also go false in the same
  // move, leaving no way to reset it otherwise).
  const activeLocationFilter = filterableLocations.some((l) => l.id === locationFilter)
    ? locationFilter
    : '';

  const visibleEmployees = activeLocationFilter
    ? employees.filter((r) => r.employee.location_id === activeLocationFilter)
    : employees;
  const visibleEmployeeIds = new Set(visibleEmployees.map((r) => r.employee.id));
  // The approval queue reads its own uncapped data (audit B1), not the capped
  // activity log, filtered by the same location selection.
  const visibleQueueFacts = activeLocationFilter
    ? queueFacts.filter((f) => visibleEmployeeIds.has(f.employee_id))
    : queueFacts;
  const visibleSummary = activeLocationFilter
    ? {
        employeeCount: visibleEmployees.length,
        totalPtoTaken: visibleEmployees.reduce((s, r) => s + r.ptoTaken, 0),
        totalPtoUpcoming: visibleEmployees.reduce((s, r) => s + r.ptoUpcoming, 0),
        overAllotmentCount: visibleEmployees.filter((r) => r.over).length,
      }
    : summary;

  // Per-employee history for the expandable table rows. recentFacts arrives
  // newest-first from the query, so each bucket inherits that order.
  const factsByEmployee = new Map<string, AbsenceFactRow[]>();
  for (const f of recentFacts) {
    const list = factsByEmployee.get(f.employee_id);
    if (list) list.push(f);
    else factsByEmployee.set(f.employee_id, [f]);
  }

  // Direction D redesign: every signed-in view renders inside AppShell's
  // sidebar/tab-bar shell — Manager Home's separate card-grid front door is
  // gone (redesign follow-up, 2026-09-04); dashboard IS the landing view
  // now, reached by '#/' the same as every stray hash (viewFromHash above).
  if (view === 'dashboard') {
    const formattedToday = fromDateKey(data.today).toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });

    return (
      <AppShell
        items={navItems}
        activeKey="dashboard"
        userName={manager.full_name}
        userInitials={initialsOf(manager.full_name)}
        isAdmin={isAdmin}
        onSignOut={signOut}
        signingOut={signingOut}
      >
        {/* Same crash-containment guarantee the old per-view header gave —
            resetKey={view}/onReset={refresh} mean exactly what they meant
            before. No homeHref here: dashboard IS the landing view now, so
            a "back to home" link pointing at '#/' would go nowhere new —
            the same reasoning Manager Home's own boundary used to apply to
            itself, before it was retired. */}
        <ErrorBoundary scope="view" resetKey={view} onReset={refresh}>
          <div style={{ ...S.dashboardPage, ...(narrow ? S.dashboardPageNarrow : null) }}>
            <div style={S.dashboardHeader}>
              <h1 style={{ ...S.dashboardTitle, ...(narrow ? S.dashboardTitleNarrow : null) }}>
                Dashboard
              </h1>
              <div style={S.dashboardHeaderRight}>
                <span style={S.dashboardDate}>{formattedToday}</span>

                {/* P6: an admin with more than one location's worth of staff
                    gets a filter that narrows the whole page — calendar,
                    pending queue, summary tiles, and the employee table
                    together. Hidden entirely for a regular manager (RLS
                    already scopes them to one location) and hidden today in
                    production, where the shop still runs a single location.
                    Relocated here from its own bar above the calendar — same
                    id/htmlFor/value/onChange, only new pill-shaped styling. */}
                {showLocationFilter && (
                  <div style={S.locationPill}>
                    <label htmlFor="location-filter" style={S.srOnly}>
                      Location
                    </label>
                    <select
                      id="location-filter"
                      value={activeLocationFilter}
                      onChange={(e) => setLocationFilter(e.target.value)}
                      style={{ ...S.locationPillSelect, ...(narrow ? S.locationPillSelectNarrow : null) }}
                    >
                      <option value="">All locations</option>
                      {filterableLocations.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={12} style={S.locationPillChevron} />
                  </div>
                )}

                {/* The MIGRATION_PLAN §5 escape hatch: everything you can see,
                    as a file you can open anywhere. RLS scopes it like any
                    read. Not shown in the approved mockup, but an existing
                    feature that must not be dropped — restyled as a pill. */}
                <button
                  onClick={() => void exportCsv(data.today)}
                  disabled={exporting}
                  style={{ ...S.exportPillBtn, ...(narrow ? S.exportPillBtnNarrow : null) }}
                >
                  {exporting ? 'Exporting…' : 'Export CSV'}
                </button>
              </div>
            </div>
            {exportError && <p style={S.dashboardExportError}>{exportError}</p>}

            {/* Order per Scott, 2026-08-11: calendar first, pending second,
                then the numbers. The standalone Activity section is gone —
                each employee's history lives in their expandable table row
                instead. */}
            <SectionCard title="Team Time-Off Calendar" ariaLabel="Team calendar">
              <TeamCalendar
                today={data.today}
                names={nameById}
                visibleIds={activeLocationFilter ? visibleEmployeeIds : null}
                reloadKey={reloadKey}
              />
            </SectionCard>

            {/* P3: the buttons replaced the live-portal link, in this exact
                spot, as the 08-11 session log planned. Approve/deny are
                definer RPCs (0011) — the decision, its audit stamp, and the
                employee's email land in one transaction, or not at all. */}
            <SectionCard title="Pending Requests" ariaLabel="Pending requests">
              <PendingRequests
                facts={visibleQueueFacts}
                resubmittedFromDates={data.resubmittedFromDates}
                names={nameById}
                employees={employees}
                onDecided={refresh}
                scopeLabel={
                  activeLocationFilter
                    ? (filterableLocations.find((l) => l.id === activeLocationFilter)?.name ?? null)
                    : null
                }
              />
            </SectionCard>

            <SectionCard title="Team Summary" ariaLabel="Team summary">
              <div style={S.tiles}>
                <Tile label="Employees" value={String(visibleSummary.employeeCount)} />
                <Tile label="PTO taken" value={fmt(visibleSummary.totalPtoTaken)} />
                <Tile label="PTO upcoming" value={fmt(visibleSummary.totalPtoUpcoming)} />
                <Tile
                  label="Over allotment"
                  value={String(visibleSummary.overAllotmentCount)}
                  alarm={visibleSummary.overAllotmentCount > 0}
                />
              </div>
            </SectionCard>

            <SectionCard title="Employees" ariaLabel="Employees">
              {/* Pre-P8 punch list, Tier 2 (mobile): this table used to drag
                  the whole page sideways on a phone. It scrolls in its own
                  box now (src/lib/TableScroller.tsx); the expandable history
                  rows stay inside it — they are part of the same <table>. */}
              <TableScroller labelledBy="employees-caption" minWidth="38rem">
                <table style={S.table}>
                  <caption id="employees-caption" style={S.srOnly}>
                    Employees
                  </caption>
                  <thead>
                    <tr>
                      <th style={S.th}>Employee</th>
                      <th style={S.th}>Points</th>
                      <th style={S.th}>PTO</th>
                      <th style={S.th}>This year</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleEmployees.map((row) => (
                      <EmployeeLine
                        key={row.employee.id}
                        row={row}
                        thresholds={thresholdsFor(row, data)}
                        showLocation={multiLocation}
                        facts={factsByEmployee.get(row.employee.id) ?? []}
                        open={openEmployee === row.employee.id}
                        onToggle={() =>
                          setOpenEmployee(openEmployee === row.employee.id ? null : row.employee.id)
                        }
                      />
                    ))}
                  </tbody>
                </table>
              </TableScroller>
            </SectionCard>
          </div>
        </ErrorBoundary>
      </AppShell>
    );
  }

  // Log an Absence, Manage Records, Roster, and the Admin Console all render
  // inside the same AppShell chrome as Dashboard — one sidebar/tab-bar, not
  // a per-view header. Their own internal content (cards, tables, buttons)
  // keeps its pre-redesign visual style for now; only the surrounding chrome
  // changed this pass. Re-skinning each page's own markup to Direction D's
  // tokens is real, separate work, same as TeamCalendar's richer per-type
  // coloring is carved out as its own follow-up rather than folded in here.
  //
  // Unconditional, not `if (view === 'log' || ...)`: the only other value
  // `view` can hold is 'dashboard', and that already returned above — so by
  // elimination this is always one of these four once execution reaches here.
  return (
    <AppShell
      items={navItems}
      activeKey={view}
      userName={manager.full_name}
      userInitials={initialsOf(manager.full_name)}
      isAdmin={isAdmin}
      onSignOut={signOut}
      signingOut={signingOut}
    >
      {/* Same crash-containment guarantee the old per-view header gave —
          resetKey={view}/onReset={refresh}/homeHref="#/" all mean exactly
          what they meant before; the chrome that survives a crash here is
          AppShell's sidebar/tab-bar instead of the old <header>. */}
      <ErrorBoundary scope="view" resetKey={view} onReset={refresh} homeHref="#/">
        {/* Same padding/font convention as the dashboard view's own
            S.dashboardPage (AppShell's own `main` slot carries no padding
            of its own), but a plain block box rather than a flex column —
            see S.viewPage's own comment for why that distinction matters
            here. Each page's own internal S.wrap (maxWidth + margin: 0
            auto) still does its own centering inside this. */}
        <div style={{ ...S.viewPage, ...(narrow ? S.viewPageNarrow : null) }}>
          {/* Pre-P8 punch list, Tier 2: LogAbsence used to receive only the
              bare EmployeeRow (name/email/location), which had no
              ptoTotal/allotment to build its own on-the-spot allotment
              warning from. Passing the full EmployeeDashboardRow lets it
              reuse the exact same unpaidWarningFor() the Dashboard's own
              Approve button uses. notifyEmployee decides whether Record's
              button claims it will notify anyone — defaults true if
              settings failed to load. */}
          {view === 'log' && (
            <LogAbsence
              employees={data.employees}
              notifyEmployee={data.settings?.notify_employee ?? true}
              onLogged={refresh}
            />
          )}

          {/* P5: void/edit/reverse a decided record (DD2/DD3/DD6). Reuses
              the Dashboard's already-loaded employee list (same pattern as
              LogAbsence), but fetches its OWN absence-day facts, unbounded
              — see features/records/data.ts's header for why reusing
              recentFacts (capped at ACTIVITY_LOG_LIMIT) would have been
              wrong for a write surface. */}
          {view === 'records' && (
            <ManageRecords
              employees={data.employees.map((r) => r.employee)}
              locations={data.locations}
              onChanged={refresh}
            />
          )}

          {/* P4: the admin console. AdminConsole re-gates on manager.role
              itself (defense in depth for UX; RLS — migration 0016's
              adm_all policies — is the real boundary), so passing manager
              down here is enough. */}
          {view === 'admin' && <AdminConsole manager={manager} onChanged={refresh} />}

          {/* Migration 0020 (pre-P8 Tier 4): the Roster, for a
              location-scoped manager. ManagerRoster re-gates on the same
              predicate the database uses (role !== 'admin' && location_id
              !== null) and does its own two-table read; `refresh` keeps
              this page's employee snapshot in step with a roster edit, the
              same reason AdminConsole takes it. */}
          {view === 'roster' && <ManagerRoster manager={manager} onChanged={refresh} />}
        </div>
      </ErrorBoundary>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

/**
 * A dashboard section as a card, so the page breaks into distinct blocks
 * (Scott's "everything runs on" feedback, 2026-08-11).
 */
function SectionCard({
  title,
  ariaLabel,
  children,
}: {
  title: string;
  ariaLabel?: string;
  children: React.ReactNode;
}) {
  const narrow = useIsNarrow();
  return (
    <section
      aria-label={ariaLabel ?? title}
      style={{ ...S.sectionCard, ...(narrow ? S.sectionCardNarrow : null) }}
    >
      <h2 style={{ ...S.sectionTitle, ...(narrow ? S.sectionTitleNarrow : null) }}>{title}</h2>
      {children}
    </section>
  );
}

function Tile({ label, value, alarm }: { label: string; value: string; alarm?: boolean }) {
  const narrow = useIsNarrow();
  return (
    <div
      style={{
        ...S.tile,
        ...(narrow ? S.tileNarrow : null),
        ...(alarm ? S.tileAlarm : null),
      }}
    >
      {/* DOM order unchanged (value, then label) — reversed visually via
          flex-direction: column-reverse below, so the mockup's "label above
          the big number" look needs no JSX reordering. */}
      <div style={{ ...S.tileValue, ...(alarm ? S.tileValueAlarm : null) }}>{value}</div>
      <div style={{ ...S.tileLabel, ...(alarm ? S.tileLabelAlarm : null) }}>{label}</div>
    </div>
  );
}

function EmployeeLine({
  row,
  thresholds,
  showLocation,
  facts,
  open,
  onToggle,
}: {
  row: EmployeeDashboardRow;
  thresholds: number[];
  showLocation: boolean;
  facts: AbsenceFactRow[];
  open: boolean;
  onToggle: () => void;
}) {
  const pills = Object.entries(row.typeCounts) as Array<[string, number]>;

  return (
    <>
      <tr>
        <td style={S.td}>
          {/* Scott's de-clutter call, 2026-08-11: the old page-level activity
              log is folded in here — click a name, get that employee's
              history. Same rows, same M8 reason labels, no second log. */}
          <button onClick={onToggle} aria-expanded={open} style={S.nameBtn}>
            <span style={S.chevron} aria-hidden="true">
              {open ? '▾' : '▸'}
            </span>
            {row.employee.full_name}
          </button>
          {showLocation && <div style={S.tdSub}>{row.locationName}</div>}
        </td>
        <td style={S.td}>
          {/* Q1, DECIDED 2026-08-20: the 10/15/20 warnings fire on the
              CALENDAR-YTD number, so the threshold colouring lives on the
              chip. Pre-P8 punch list, Tier 1: the second "rolling 12-mo"
              number that used to sit below it drove nothing and only
              confused a real click-through into thinking scoring might
              still run on the wrong window — removed from the UI. The
              rolling window itself is untouched (`data.ts`'s
              `activePoints`, `active_points()`); both windows still exist
              and still differ on purpose, this only stops showing the inert
              one. */}
          <span style={{ ...S.pointsChip, ...thresholdStyle(row.points, thresholds) }}>
            {fmt(row.points)} YTD
            {/* Pre-P8 punch list, Tier 3: colour was the ONLY thing separating
                the Verbal and Written tiers (amber vs orange) — invisible to
                anyone colour-blind, and to anyone who does not know the code.
                Name the tier in words, the same words Company Settings uses
                for the three thresholds. */}
            {tierLabel(row.points, thresholds) && (
              <span style={S.pointsTier}> · {tierLabel(row.points, thresholds)}</span>
            )}
          </span>
        </td>
        <td style={S.td}>
          <PtoChip row={row} />
        </td>
        <td style={S.td}>
          {pills.length === 0 ? (
            <span style={S.tdSub}>—</span>
          ) : (
            pills.map(([type, n]) => (
              <span key={type} style={S.pill}>
                {type} {n}
              </span>
            ))
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={4} style={S.historyCell}>
            {/* Inside the sideways-scrolling box the row is >= 38rem wide, but
                this is prose, not columns: pin it to the visible edge (sticky
                left) and cap its width so the lines wrap where the reader is
                looking instead of running off to the right (review finding). */}
            <div style={S.historyInner}>
              {facts.length === 0 ? (
                <p style={{ ...S.tdSub, margin: 0 }}>No absence records.</p>
              ) : (
                <ul style={S.log}>
                  {facts.map((f) => (
                    <ActivityLine key={f.id} f={f} name={row.employee.full_name} showName={false} />
                  ))}
                </ul>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * What is waiting on a decision, grouped by request — with the P3 buttons.
 *
 * Each decision calls its definer RPC and renders the message the database
 * sent back verbatim ("Approved - X has been emailed.", "That request is not
 * pending — it is already Approved.", …), then refreshes the data quietly.
 *
 * The v22 over-allotment PROJECTION runs before anyone clicks: a pending PTO
 * request whose approval would push the employee past their allotment gets
 * the live portal's warning line, using the RAW pto_days on the pending rows
 * (counted_pto_days is rightly 0 while Pending — the projection asks "what if
 * approved"). It warns; it never blocks — same as v22.
 */
function PendingRequests({
  facts,
  resubmittedFromDates,
  names,
  employees,
  onDecided,
  scopeLabel,
}: {
  facts: AbsenceFactRow[];
  resubmittedFromDates: Record<string, string[]>;
  names: Map<string, string>;
  employees: EmployeeDashboardRow[];
  onDecided: () => void;
  /** The active location filter's name, when one is set — the empty state
      says so, since "nothing waiting" under a filter is not "nothing waiting"
      (pre-P8 punch list, Tier 3; dormant with one location in production). */
  scopeLabel: string | null;
}) {
  // Pre-P8 punch list, Tier 2: tracking just the request id made a Deny click
  // show "Working…" on the ADJACENT Approve button (both read the same id).
  // Track which action is in flight too, so each button only ever labels its
  // own click.
  const [busy, setBusy] = useState<{ id: string; action: 'approve' | 'deny' } | null>(null);
  const [result, setResult] = useState<ActionResult | null>(null);

  const rowByEmployee = new Map(employees.map((r) => [r.employee.id, r]));

  const byRequest = new Map<string, AbsenceFactRow[]>();
  for (const f of facts) {
    if (f.status !== 'Pending') continue;
    const list = byRequest.get(f.request_id);
    if (list) list.push(f);
    else byRequest.set(f.request_id, [f]);
  }

  // Pre-P8 punch list, Tier 2: neither button asked for confirmation before
  // firing an RPC that immediately emails the employee. `confirmText` is
  // built by the caller (which already has the name/type/date range on
  // hand for display) rather than recomputed here.
  async function decide(requestId: string, action: 'approve' | 'deny', confirmText: string) {
    if (!window.confirm(confirmText)) return;
    setBusy({ id: requestId, action });
    setResult(null);
    const res = await (action === 'approve' ? approveRequest(requestId) : denyRequest(requestId));
    setBusy(null);
    setResult(res);
    // Refresh even on a refusal: "already Approved" means this list is stale,
    // and the freshest cure is the data itself.
    onDecided();
  }

  if (byRequest.size === 0) {
    return (
      <>
        {result && <ResultLine result={result} />}
        <p style={S.sub}>
          {scopeLabel
            ? `Nothing is waiting on a decision at ${scopeLabel}. Choose “All locations” above to check the others.`
            : 'Nothing is waiting on a decision.'}
        </p>
      </>
    );
  }

  return (
    <>
      {result && <ResultLine result={result} />}
      <ul style={S.log}>
        {[...byRequest.values()].map((days) => {
          days.sort((a, b) => a.absence_date.localeCompare(b.absence_date));
          const first = days[0];
          if (first === undefined) return null;
          const last = days[days.length - 1] ?? first;

          // The v22 approve-time projection: current YTD PTO total plus THIS
          // request's raw days, against the resolved allotment.
          const empRow = rowByEmployee.get(first.employee_id);
          const requestDays = days.reduce((s, d) => s + d.pto_days, 0);
          const name = names.get(first.employee_id) ?? '(former employee)';

          // COVERAGE OVERLAP (Scott's tinker feedback, 2026-08-24): who else
          // is already out on these dates? Same-employee same-day duplicates
          // are structurally impossible (one_live_absence_per_day), so any
          // hit here is a DIFFERENT person — at a ten-person shop, two techs
          // out at once is exactly what a manager wants shoved in their face
          // before clicking Approve. Denied/Voided don't count as "out";
          // other PENDING requests do, marked as tentative. Computed from the
          // uncapped queue window (queueFacts: every Pending day plus every
          // live day across the same date span), not the capped activity log.
          //
          // Adversarial-review fix: scoped to the SAME LOCATION as this
          // request's own employee, via `rowByEmployee` — which is always the
          // FULL unfiltered roster, so this is correct independent of
          // whatever the viewer's own on-screen location filter happens to be
          // set to. Two people at different shops off the same day are not a
          // coverage conflict; a former employee (no entry in `rowByEmployee`
          // at all) can't be attributed to either side, so never counts.
          const firstLocation = rowByEmployee.get(first.employee_id)?.employee.location_id;
          const overlaps = new Map<string, { dates: string[]; tentative: boolean }>();
          const requestDates = new Set(days.map((d) => d.absence_date));
          for (const f of facts) {
            if (f.request_id === first.request_id) continue;
            if (f.status === 'Denied' || f.status === 'Voided') continue;
            if (!requestDates.has(f.absence_date)) continue;
            const otherLocation = rowByEmployee.get(f.employee_id)?.employee.location_id;
            if (firstLocation === undefined || otherLocation === undefined) continue;
            if (otherLocation !== firstLocation) continue;
            const who = names.get(f.employee_id) ?? '(former employee)';
            const entry = overlaps.get(who) ?? { dates: [], tentative: false };
            entry.dates.push(f.absence_date);
            if (f.status === 'Pending') entry.tentative = true;
            overlaps.set(who, entry);
          }
          const overlapWarning =
            overlaps.size === 0
              ? null
              : 'Coverage overlap — also out on these dates: ' +
                [...overlaps.entries()]
                  .map(
                    ([who, o]) =>
                      `${who} (${o.dates.sort().join(', ')}${o.tentative ? ' — pending' : ''})`,
                  )
                  .join('; ') +
                '.';

          const unpaidWarning =
            first.absence_type === 'PTO' && empRow !== undefined
              ? unpaidWarningFor(name, empRow.ptoTotal, empRow.allotment, requestDays)
              : null;

          const anyBusy = busy !== null;
          const thisBusyApprove = busy?.id === first.request_id && busy.action === 'approve';
          const thisBusyDeny = busy?.id === first.request_id && busy.action === 'deny';

          // Pre-P8 punch list, Tier 2: neither action had a confirmation
          // step before firing an RPC that immediately emails the
          // employee. Built once here (name/type/date range are all
          // already on hand) and reused for both buttons.
          const dateRangeText =
            last.absence_date !== first.absence_date
              ? `${first.absence_date} → ${last.absence_date}`
              : first.absence_date;
          const confirmApprove = `Approve ${name}'s ${first.absence_type} request (${dateRangeText})? ${name} will be emailed.`;
          const confirmDeny = `Deny ${name}'s ${first.absence_type} request (${dateRangeText})? ${name} will be emailed.`;

          // Scott, 2026-10-30: the manager alert email carried the request's
          // Notes, but this list never showed them — and Manage Records skips
          // Pending — so the portal had nowhere to read a note before the
          // decision it is meant to inform. Notes and submitted_at live on the
          // parent request, so every day row carries the same values.
          const requestedText = first.submitted_at
            ? formatRequested(first.submitted_at, first.absence_date)
            : null;
          // 0032: sent from the denial email's "request a different date" link
          // — name the denied dates, when this manager can read them.
          const originDates = first.resubmitted_from
            ? resubmittedFromDates[first.resubmitted_from]
            : undefined;
          const originFirst = originDates?.[0];
          const originLast = originDates?.[originDates.length - 1];
          const resubmitText = first.resubmitted_from
            ? 'Resubmitted after a denied request' +
              (originFirst
                ? ` for ${originFirst}${originLast && originLast !== originFirst ? ` → ${originLast}` : ''}`
                : '')
            : null;
          const metaLine = [requestedText && `Requested ${requestedText}`, resubmitText]
            .filter(Boolean)
            .join(' · ');

          return (
            <li key={first.request_id} style={S.pendingItem}>
              <div style={S.logLine}>
                <span style={{ fontWeight: 600 }}>{name}</span>
                <span>{first.absence_type}</span>
                <span style={S.logDate}>
                  {first.absence_date}
                  {last.absence_date !== first.absence_date ? ` → ${last.absence_date}` : ''}
                </span>
                <span style={S.logMeta}>
                  {days.length} day{days.length === 1 ? '' : 's'}
                  {first.absence_type === 'PTO' && requestDays !== days.length
                    ? ` (${fmt(requestDays)} PTO)`
                    : ''}
                </span>
                <span style={S.pendingActions}>
                  <button
                    onClick={() => void decide(first.request_id, 'approve', confirmApprove)}
                    disabled={anyBusy}
                    style={S.approveBtn}
                  >
                    {thisBusyApprove ? 'Working…' : 'Approve'}
                  </button>
                  <button
                    onClick={() => void decide(first.request_id, 'deny', confirmDeny)}
                    disabled={anyBusy}
                    style={S.denyBtn}
                  >
                    {thisBusyDeny ? 'Working…' : 'Deny'}
                  </button>
                </span>
              </div>
              {metaLine && <p style={S.pendingRequested}>{metaLine}</p>}
              {first.notes && (
                <p style={S.pendingNote}>
                  <strong>Notes:</strong> {first.notes}
                </p>
              )}
              {overlapWarning && <p style={S.unpaidWarning}>{overlapWarning}</p>}
              {unpaidWarning && <p style={S.unpaidWarning}>{unpaidWarning}</p>}
            </li>
          );
        })}
      </ul>
    </>
  );
}

/**
 * The RPC's own wording, success or refusal — never paraphrased. A failure
 * (network, server — `kind: 'failure'`) reads as a plain sentence with the
 * raw wire text tucked behind "Technical details" (pre-P8 punch list, Tier
 * 2: a bare "TypeError: Failed to fetch" used to render here styled exactly
 * like a designed refusal). Errors announce as an alert, successes as status.
 */
function ResultLine({ result }: { result: ActionResult }) {
  return (
    <div style={result.ok ? S.actionOk : S.actionErr}>
      {/* The live region is the sentence only; the disclosure sits beside it so
          opening it never re-announces the message (alert is atomic). */}
      <p role={result.ok ? 'status' : 'alert'} style={S.resultText}>
        {result.ok ? (result.message ?? 'Done.') : (result.error ?? 'Something went wrong.')}
      </p>
      {!result.ok && <TechnicalDetails detail={result.detail} />}
    </div>
  );
}

/** DD1: "X taken · Y upcoming · Z left", with the v22 red over-allotment state. */
function PtoChip({ row }: { row: EmployeeDashboardRow }) {
  const base = `${fmt(row.ptoTaken)} taken · ${fmt(row.ptoUpcoming)} upcoming`;

  if (row.allotment === null) {
    // Today's live state: every allotment cell is blank = no limit configured.
    return <span style={{ ...S.chip, ...S.chipNeutral }}>{base} · no limit set</span>;
  }
  if (row.over) {
    // remaining is negative here; say "N over", not "-N left" (v22 wording).
    return (
      <span style={{ ...S.chip, ...S.chipOver }}>
        {base} · {fmt(-(row.remaining ?? 0))} over
      </span>
    );
  }
  return (
    <span style={{ ...S.chip, ...S.chipOk }}>
      {base} · {fmt(row.remaining ?? 0)} left
    </span>
  );
}

function ActivityLine({ f, name, showName = true }: { f: AbsenceFactRow; name: string; showName?: boolean }) {
  const voided = f.status === 'Voided';
  return (
    <li style={{ ...S.logLine, ...(voided ? S.logVoided : null) }}>
      <span style={S.logDate}>{f.absence_date}</span>
      {showName && <span style={{ fontWeight: 600 }}>{name}</span>}
      <span>{f.absence_type}</span>
      <span style={S.statusPill}>{f.status}</span>
      {!f.is_taken && !voided && <span style={S.upcomingPill}>upcoming</span>}
      {f.counted_points > 0 && <span style={S.logMeta}>{fmt(f.counted_points)} pts</span>}
      {f.counted_pto_days > 0 && (
        <span style={S.logMeta}>{fmt(f.counted_pto_days)} PTO day{f.counted_pto_days === 1 ? '' : 's'}</span>
      )}
      {/* M8's whole point: an exclusion is a labelled reason, never a silent
          zero. §5: "the reason is shown, not just the absence of a number".
          EXCEPT not_pto (2026-08-05, the first real Sick-with-points row): it
          is a category fact — a Sick day self-evidently earns no PTO day — not
          an exclusion, and rendering it beside "5 pts" read as if the POINTS
          were not counted. The real exclusion reasons stay loud. */}
      {f.not_counted_reason && f.not_counted_reason !== 'not_pto' && (
        <span style={S.notCounted}>{notCountedLabel(f.not_counted_reason, f.not_counted_note)}</span>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** location → company → the documented 10/15/20 default (same chain as v22). */
function thresholdsFor(row: EmployeeDashboardRow, data: DashboardData): number[] {
  const location = data.locations.find((l) => l.id === row.employee.location_id);
  return location?.point_thresholds ?? data.settings?.point_thresholds ?? [10, 15, 20];
}

/**
 * Colour the YTD chip as it crosses thresholds — Q1's window (2026-08-20).
 * Every colour here is ≥4.5:1 on the chip's #f1f1f1 (pre-P8 punch list,
 * Tier 3): the Written tier was #c25e00, 3.7:1, and is now #a84f00, 4.9:1.
 * Verbal's #946200 is 4.6:1 and Final's #b00020 6.5:1 — unchanged.
 */
function thresholdStyle(points: number, thresholds: number[]): React.CSSProperties {
  switch (tierFor(points, thresholds)) {
    case 3:
      return { color: '#b00020', fontWeight: 700 };
    case 2:
      return { color: '#a84f00', fontWeight: 600 };
    case 1:
      return { color: '#946200', fontWeight: 600 };
    default:
      return {};
  }
}

/** The tier's name, in Company Settings' own words ("Verbal, Written, Final"), or null below the first. */
function tierLabel(points: number, thresholds: number[]): string | null {
  switch (tierFor(points, thresholds)) {
    case 3:
      return 'Final';
    case 2:
      return 'Written';
    case 1:
      return 'Verbal';
    default:
      return null;
  }
}

/** The ONE place the three thresholds are compared, so colour and word can never disagree. */
function tierFor(points: number, thresholds: number[]): 0 | 1 | 2 | 3 {
  const [t1, t2, t3] = [thresholds[0] ?? 10, thresholds[1] ?? 15, thresholds[2] ?? 20];
  if (points >= t3) return 3;
  if (points >= t2) return 2;
  if (points >= t1) return 1;
  return 0;
}

/**
 * "Oct 29, 9:14 PM" in the viewer's own time zone; the year is added only when
 * it differs from the request's first day, whose YYYY-MM-DD is already on
 * screen. submitted_at is a full timestamp, not a bare date key, so new Date()
 * is safe here (trap #1 is about YYYY-MM-DD strings).
 */
function formatRequested(submittedAt: string, firstDay: string): string {
  const d = new Date(submittedAt);
  const sameYear = String(d.getFullYear()) === firstDay.slice(0, 4);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** 0.5 stays "0.5"; 4 stays "4" — never "4.0". */
function fmt(n: number): string {
  return String(n);
}

/** "Scott Reber" → "SR" for the sidebar/tab-bar avatar chip; a single-word name keeps its first two letters. */
function initialsOf(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return '?';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? '';
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

// ---------------------------------------------------------------------------
// Styles — same conventions as LoginScreen/App: system-ui, quiet greys.
// ---------------------------------------------------------------------------

const S = {
  center: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'system-ui, sans-serif',
    color: '#666',
    padding: '1.5rem',
  },
  panel: {
    maxWidth: '26rem',
    border: '1px solid #e3e7ee',
    borderRadius: '0.75rem',
    padding: '2rem',
    color: '#1a1a1a',
    background: 'white',
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.05)',
  },
  panelH: { marginTop: 0, fontSize: '1.1rem' },
  panelP: { fontSize: '0.9rem', lineHeight: 1.5, color: '#444' },
  code: { fontSize: '0.8rem', wordBreak: 'break-all' },
  sub: { marginTop: '0.2rem', color: '#666', fontSize: '0.85rem' },
  // Redesign follow-up #1: the padding/font convention shared by Log an
  // Absence, Manage Records, Roster, and Admin Console's AppShell wrapper.
  // Deliberately NOT display:flex like dashboardPage below — each of those
  // views renders exactly one child, so there is no stacking/gap to manage,
  // and a flex container here would make that one child a flex ITEM with the
  // default min-width:auto. That default lets a flex item's min-content
  // size include descendants that would otherwise correctly clip/scroll —
  // in practice, TableScroller's own overflow-x:auto box stopped containing
  // the Roster/Managers/Locations tables and the whole page grew wide enough
  // to blow out AppShell's fixed bottom tab bar on a phone (caught in the
  // real mobile click-through, not by typecheck/build). A plain block box
  // has no such quirk: width:auto here simply fills the available space
  // regardless of a descendant's content size.
  viewPage: {
    boxSizing: 'border-box',
    padding: '36px 40px 48px',
    fontFamily: font.body,
    color: color.ink,
  },
  viewPageNarrow: { padding: '18px 16px 24px' },
  // ---- Dashboard view only (Direction D redesign) ----
  // maxWidth + margin: '0 auto' matches AdminConsole's own S.wrap (960px) —
  // the other content-dense, multi-section screen. Without it this was the
  // one view stretching edge-to-edge instead of centering like every other
  // screen (found alongside ManageRecords' own missing margin, same report).
  dashboardPage: {
    flex: '1 1 auto',
    minWidth: 0,
    maxWidth: 960,
    margin: '0 auto',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    padding: '36px 40px 48px',
    fontFamily: font.body,
    color: color.ink,
  },
  dashboardPageNarrow: { padding: '18px 16px 24px', gap: '16px' },
  dashboardHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '12px',
  },
  dashboardTitle: {
    margin: 0,
    fontSize: '30px',
    fontWeight: 700,
    letterSpacing: '-0.01em',
    fontFamily: font.display,
    color: color.ink,
  },
  dashboardTitleNarrow: { fontSize: '23px' },
  dashboardHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    flexWrap: 'wrap',
  },
  dashboardDate: { fontSize: '13px', color: color.textMuted },
  // The relocated location filter — same id/htmlFor/value/onChange as
  // before, only new pill-shaped wrapper CSS with a custom chevron overlay
  // (the native <select> arrow is hidden via `appearance: none`).
  locationPill: { position: 'relative', display: 'inline-flex', alignItems: 'center' },
  locationPillSelect: {
    appearance: 'none',
    WebkitAppearance: 'none',
    fontSize: '12.5px',
    fontWeight: 600,
    fontFamily: font.body,
    color: color.textSubtle,
    background: color.pillNeutralBg,
    border: 0,
    borderRadius: radius.pill,
    // 1rem = 16px would stop iOS Safari's focus auto-zoom, but this control
    // reads as a small pill, not a form field — the zoom is an acceptable
    // trade the location filter already made before this redesign.
    padding: '7px 30px 7px 14px',
    cursor: 'pointer',
  },
  // Touch-target fix (adversarial review, 2026-09-04): the pill's own 29px
  // desktop height is fine for a mouse, but on a phone this is the app's
  // only interactive control under the 2.75rem/44px minimum enforced
  // everywhere else (TeamCalendar's nav/today/retry buttons, the approve/
  // deny pills). Only the narrow branch grows — desktop keeps the compact
  // pill.
  locationPillSelectNarrow: { minHeight: '2.75rem', padding: '11px 30px 11px 14px' },
  locationPillChevron: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: color.textSubtle,
    pointerEvents: 'none',
  },
  exportPillBtn: {
    fontSize: '12.5px',
    fontWeight: 600,
    fontFamily: font.body,
    color: color.textSubtle,
    background: color.pillNeutralBg,
    border: 0,
    borderRadius: radius.pill,
    padding: '7px 16px',
    cursor: 'pointer',
  },
  // Touch-target fix (adversarial review, 2026-09-04): see
  // locationPillSelectNarrow — same 44px floor, narrow-only.
  exportPillBtnNarrow: { minHeight: '2.75rem', padding: '11px 16px' },
  dashboardExportError: { margin: '-12px 0 0', fontSize: '0.78rem', color: '#b00020' },
  button: {
    padding: '0.55rem 1.1rem',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: 'white',
    background: '#1a1a1a',
    border: 0,
    borderRadius: '0.4rem',
    cursor: 'pointer',
  },
  sectionCard: {
    border: `1px solid ${color.cardBorder}`,
    borderRadius: radius.cardDesktop,
    padding: '26px 28px 22px',
    background: color.cardBg,
    boxShadow: shadow.card,
  },
  sectionCardNarrow: { borderRadius: radius.cardMobile, padding: '16px', boxShadow: 'none' },
  sectionTitle: {
    // Longhand, not the `margin` shorthand: sectionTitleNarrow overrides just
    // marginBottom below, and mixing shorthand/longhand for the same value
    // across a re-render is a React style-diffing footgun (its own dev-mode
    // warning: "don't mix shorthand and non-shorthand properties").
    marginTop: 0,
    marginRight: 0,
    marginBottom: '18px',
    marginLeft: 0,
    fontSize: '17px',
    fontWeight: 700,
    fontFamily: font.display,
    color: color.ink,
  },
  sectionTitleNarrow: { fontSize: '15px', marginBottom: '14px' },
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(9rem, 1fr))',
    gap: '16px',
  },
  tile: {
    display: 'flex',
    flexDirection: 'column-reverse',
    gap: '7px',
    border: `1px solid ${color.cardBorder}`,
    borderRadius: radius.tileDesktop,
    padding: '20px',
    background: color.cardBg,
    boxShadow: shadow.card,
  },
  tileNarrow: { borderRadius: radius.tileMobile, padding: '14px', boxShadow: 'none' },
  tileAlarm: { border: `1px solid ${color.amberBorder}`, background: color.amberBg, boxShadow: 'none' },
  tileValue: { fontSize: '28px', fontWeight: 700, fontFamily: font.display, color: color.ink },
  tileValueAlarm: { color: color.amberDark },
  tileLabel: {
    fontSize: '11.5px',
    fontWeight: 700,
    color: color.textFaint,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  tileLabelAlarm: { color: color.amberTileLabel },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13.5px',
    fontFamily: font.body,
    color: color.ink,
  },
  th: {
    textAlign: 'left',
    fontSize: '10.5px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: color.textFaint,
    borderBottom: `1px solid ${color.cardBorder}`,
    padding: '0.5rem 0.75rem 0.5rem 0',
  },
  td: {
    borderBottom: `1px solid ${color.pageBg}`,
    padding: '0.65rem 0.75rem 0.65rem 0',
    verticalAlign: 'top',
  },
  tdSub: { fontSize: '0.78rem', color: color.textMuted, marginTop: '0.15rem' },
  nameBtn: {
    padding: 0,
    border: 0,
    background: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 600,
    color: color.ink,
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: '0.35rem',
    textAlign: 'left',
  },
  chevron: { color: color.brandBlue, fontSize: '1rem', flexShrink: 0 },
  // Visually hidden, still read by screen readers (the standard clip trick).
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  historyInner: { position: 'sticky', left: 0, maxWidth: 'calc(100vw - 6rem)' },
  historyCell: {
    borderBottom: `1px solid ${color.pageBg}`,
    padding: '0.25rem 0.75rem 0.9rem 1.5rem',
    background: color.pageBg,
  },
  pointsChip: {
    display: 'inline-block',
    padding: '0.15rem 0.55rem',
    borderRadius: radius.pill,
    // Near-identical luminance to the old #f1f1f1 — the three tier text
    // colors below were tuned for that background and are UNCHANGED.
    background: color.pillNeutralBg,
    fontWeight: 600,
    fontSize: '0.85rem',
    whiteSpace: 'nowrap',
  },
  pointsTier: { fontWeight: 600 },
  chip: {
    display: 'inline-block',
    padding: '0.15rem 0.55rem',
    borderRadius: radius.pill,
    fontSize: '0.82rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  // "OK / under allotment" moves to brand blue — the spec's explicit
  // "PTO status → brand blue" mapping (a deliberate departure from the old
  // green-means-good chip; flagged in the redesign's risk list).
  chipOk: { background: color.chipBrandBg, color: color.brandBlue },
  // Over-allotment stays in the red family — the spec does not redefine an
  // error/over-limit accent, so no new red is introduced here.
  chipOver: { background: '#fdecef', color: '#b00020' },
  chipNeutral: { background: color.pillNeutralBg, color: color.textSubtle },
  pill: {
    display: 'inline-block',
    marginRight: '0.35rem',
    marginBottom: '0.25rem',
    padding: '0.1rem 0.5rem',
    borderRadius: radius.pill,
    border: `1px solid ${color.cardBorder}`,
    fontSize: '0.75rem',
    color: color.textSubtle,
    whiteSpace: 'nowrap',
  },
  log: { listStyle: 'none', margin: 0, padding: 0, fontSize: '0.87rem' },
  logLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: '0.6rem',
    padding: '0.6rem 0',
    borderBottom: `1px solid ${color.pageBg}`,
  },
  logVoided: { color: color.textFaint, textDecoration: 'line-through' },
  logDate: { fontVariantNumeric: 'tabular-nums', color: color.textMuted, minWidth: '6.2rem' },
  logMeta: { color: color.textMuted, fontSize: '0.8rem' },
  statusPill: {
    padding: '0.05rem 0.45rem',
    borderRadius: radius.pill,
    border: `1px solid ${color.cardBorder}`,
    fontSize: '0.72rem',
    color: color.textSubtle,
  },
  upcomingPill: {
    padding: '0.05rem 0.45rem',
    borderRadius: radius.pill,
    background: color.chipBrandBg,
    color: color.brandBlue,
    fontSize: '0.72rem',
    fontWeight: 600,
  },
  notCounted: {
    fontStyle: 'italic',
    color: color.amberDark,
    fontSize: '0.8rem',
  },
  pendingItem: { borderBottom: `1px solid ${color.pageBg}` },
  // Pulled up under the row it describes (logLine's own bottom padding would
  // otherwise float it halfway to the next block).
  pendingRequested: { margin: '-0.35rem 0 0.55rem', fontSize: '0.8rem', color: color.textMuted },
  // Neutral grey on purpose: amber means "warning" on this card, and the
  // employee's note is information, not a warning. pre-wrap keeps their line
  // breaks; overflowWrap stops one long unbroken word widening the card.
  pendingNote: {
    margin: '0 0 0.55rem',
    padding: '0.45rem 0.7rem',
    borderRadius: '0.6rem',
    background: color.pageBg,
    border: `1px solid ${color.cardBorder}`,
    color: color.ink,
    fontSize: '0.85rem',
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  pendingActions: {
    marginLeft: 'auto',
    display: 'inline-flex',
    gap: '0.5rem',
    flexShrink: 0,
  },
  // Solid blue pill (Approve) / white pill with a light gray border (Deny) —
  // the spec's pill-button rule. Still >= 2.75rem tall (Pre-P8 punch list,
  // Tier 2's touch-target fix), just reshaped.
  approveBtn: {
    padding: '0.55rem 1.1rem',
    minHeight: '2.75rem',
    fontSize: '0.9rem',
    fontWeight: 700,
    fontFamily: font.display,
    color: 'white',
    background: color.brandBlue,
    border: 0,
    borderRadius: radius.pill,
    cursor: 'pointer',
  },
  denyBtn: {
    padding: '0.55rem 1.1rem',
    minHeight: '2.75rem',
    fontSize: '0.9rem',
    fontWeight: 700,
    fontFamily: font.display,
    color: color.textSubtle,
    background: 'white',
    border: `1.5px solid ${color.cardBorder}`,
    borderRadius: radius.pill,
    cursor: 'pointer',
  },
  unpaidWarning: {
    margin: '0 0 0.55rem',
    padding: '0.45rem 0.7rem',
    borderRadius: '0.6rem',
    background: color.amberBg,
    color: color.amberDark,
    border: `1px solid ${color.amberBorder}`,
    fontSize: '0.82rem',
    lineHeight: 1.45,
  },
  resultText: { margin: 0 },
  actionOk: {
    margin: '0 0 0.9rem',
    padding: '0.55rem 0.8rem',
    borderRadius: '0.6rem',
    background: '#e9f5ec',
    color: '#1c6b32',
    fontSize: '0.87rem',
  },
  actionErr: {
    margin: '0 0 0.9rem',
    padding: '0.55rem 0.8rem',
    borderRadius: '0.6rem',
    background: '#fdecef',
    color: '#b00020',
    fontSize: '0.87rem',
  },
} satisfies Record<string, React.CSSProperties>;
